import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Chip,
  Box,
  TextField,
  InputAdornment,
  Pagination,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import GetAppIcon from "@mui/icons-material/GetApp";
import BidIcon from "../../../web-app/src/assets/img/bid.svg";
import CancelIcon from "../../../web-app/src/assets/img/close.svg";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  FONT_FAMILY,
  SECONDORY_COLOR,
  downloadCsv,
} from "../common/sharedFunctions";
import { colors } from "../components/Theme/WebTheme";
import { debounce } from "lodash";

// Utility function for formatting dates consistently
const formatDate = (dateValue, lang = 'en-US') => {
  if (!dateValue) return "";

  if (typeof dateValue === "string") return dateValue;

  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return ""; // Invalid date

  const day = date.getDate();
  const month = date.toLocaleString(lang, { month: "short" });
  const year = date.getFullYear();
  const time = date
    .toLocaleString(lang, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();

  return `${day} ${month} ${year} - ${time}`;
};

const BookingTable = (props) => {
  const {
    data,
    columns,
    onRowClick,
    onCancelBooking,
    onSelectBid,
    onAssignDriver,
    onPaymentProcess,
    onForceEnd,
    onViewDetails,
    settings,
    role,
    showEst,
    currentPage = 0,
    onChangePage,
    isRTL,
    t,
    i18n, 
    totalRecords: serverTotalRecords,
    loading = false,
    onRowsPerPageChange,
    onSearch,
  } = props;

  const currentLang = i18n ? i18n.language : 'en-US';

  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(currentPage);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [filteredData, setFilteredData] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);

  // Sync local page state with currentPage prop
  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  // Apply search filtering when data or searchQuery changes
  useEffect(() => {
    if (!data) return;

    if (!searchQuery.trim()) {
      setFilteredData(data);
      setIsSearchActive(false);
      return;
    }

    setIsSearchActive(true);
    const filtered = data.filter((row) => {
      return columns
        .filter((col) => col.field !== "otp")
        .some((column) => {
          const fieldValue = row[column.field];
          if (fieldValue === undefined || fieldValue === null) return false;

          // Handle special case for status (for searching by translated status)
          if (column.field === "status") {
            return t(fieldValue.toLowerCase())
              .toLowerCase()
              .includes(searchQuery.toLowerCase());
          }

          // Handle special case for vehicle type (for searching by translated vehicle type)
          if (column.field === "carType" || column.field === "vehicleType") {
            return t(fieldValue.toLowerCase())
              .toLowerCase()
              .includes(searchQuery.toLowerCase());
          }

          // Handle date fields
          if (column.field === "bookingDate" || column.field === "tripdate") {
            return formatDate(fieldValue, currentLang)
              .toLowerCase()
              .includes(searchQuery.toLowerCase());
          }

          // Handle different types of values
          if (typeof fieldValue === "object") {
            return false;
          }

          // Convert field value to string and perform case-insensitive search
          return String(fieldValue)
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        });
    });

    setFilteredData(filtered);
  }, [data, searchQuery, columns, t, currentLang]);

  // Debounced search function for server-side filtering
  const debouncedSearch = useCallback(
    debounce((query) => {
      if (onSearch) {
        onSearch(query);
      }
    }, 500),
    [onSearch]
  );

  // Handle search input changes
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // If server-side search is provided, use it
    if (onSearch) {
      debouncedSearch(query);
    }
    
    // Always reset to first page when searching
    setPage(0);
    if (onChangePage) onChangePage(0);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setIsSearchActive(false);
    if (onSearch) {
      onSearch("");
    }
    setPage(0);
    if (onChangePage) onChangePage(0);
  };

  const getStatusChip = (status) => {
    let chipColor = "#666";
    let chipBgColor = "#f0f0f0";

    // Set colors based on status
    switch (status) {
      case "NEW":
        chipColor = "#6226EF";
        chipBgColor = "#E0D4FC";
        break;
      case "CANCELLED":
        chipColor = "#ffffff";
        chipBgColor = "#B3261E";
        break;
      case "COMPLETE":
        chipColor = "#e6ffe6";
        chipBgColor = "#00B69B";
        break;
      case "ACCEPTED":
        chipColor = "#5570F1";
        chipBgColor = "#E7EBFF";
        break;
      case "STARTED":
        chipColor = "#EAB30D";
        chipBgColor = "#FEF9EA";
        break;
      case "REACHED":
        chipColor = "#ffffff";
        chipBgColor = "#4CAF50";
        break;
      case "PICKEDUP":
        chipColor = "#ffffff";
        chipBgColor = "#2196F3";
        break;
      case "ARRIVED":
        chipColor = "#ffffff";
        chipBgColor = "#795548";
        break;
      case "PAID":
        chipColor = "#19692c";
        chipBgColor = "#E9F6EC";
        break;
      default:
        chipColor = "#666";
        chipBgColor = "#f0f0f0";
    }

    return (
      <Chip
        label={t(status)} 
        sx={{
          backgroundColor: chipBgColor,
          color: chipColor,
          borderRadius: "16px",
          fontWeight: 500,
          minWidth: "80px",
          textAlign: "center",
        }}
      />
    );
  };

  const handleRowClick = (row, index) => {
    setSelectedRow(index);
    if (onRowClick) onRowClick(row);
  };

  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPage(newPage - 1);
    if (onChangePage) onChangePage(newPage - 1);
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);

    if (onRowsPerPageChange) {
      onRowsPerPageChange(newRowsPerPage);
    }
    
    if (onChangePage) onChangePage(0);
  };

  // Export to CSV
  const exportCSV = () => {
    const csvData = [
      columns
        .filter(col => col.field !== "otp")
        .map(col => col.title)
        .join(','),
      ...filteredData.map(row => 
        columns
          .filter(col => col.field !== "otp")
          .map(col => {
            if (col.field === 'bookingDate' || col.field === 'tripdate') {
              return `"${formatDate(row[col.field], currentLang)}"`;
            }
            if (col.field === 'status') {
              return `"${t(row[col.field].toLowerCase())}"`;
            }
            if (col.field === 'carType' || col.field === 'vehicleType') {
              return `"${t(row[col.field].toLowerCase())}"`;
            }
            if (col.field === 'trip_cost' || col.field === 'cost' || col.field === 'driver_share') {
              return settings && settings.symbol
                ? settings.swipe_symbol === false
                  ? settings.symbol + row[col.field]
                  : row[col.field] + settings.symbol
                : row[col.field];
            }
            return `"${row[col.field] || ''}"`;
          })
          .join(',')
      )
    ].join('\n');

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'booking_history.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Calculate data to display
  const dataToDisplay = () => {
    // When searching, show all results (no pagination)
    if (isSearchActive && searchQuery) {
      return filteredData;
    }

    // When not searching, paginate normally
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  };

  // Calculate pagination values
  const recordCount = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(recordCount / rowsPerPage));
  const currentPageNumber = Math.min(Math.max(1, page + 1), totalPages);
  const startRecord = isSearchActive 
    ? 1 
    : Math.min(recordCount, (currentPageNumber - 1) * rowsPerPage + 1);
  const endRecord = isSearchActive 
    ? recordCount 
    : Math.min(recordCount, currentPageNumber * rowsPerPage);
  const paginationLabel = `${recordCount > 0 ? startRecord : 0}-${endRecord} of ${recordCount}`;

  return (
    <>
      <Typography
        variant="h5"
        sx={{
          fontWeight: "600",
          fontSize: "30px",
          mt: 3,
          color: "#333",
        }}
      >
        {t("booking_history")}
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Box></Box> {/* Placeholder for left alignment */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            placeholder={t("search")}
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{
              width: "250px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                bgcolor: "#fff",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="clear search"
                    onClick={handleClearSearch}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton 
            size="small" 
            onClick={exportCSV}
            sx={{ ml: 1 }}
          >
            <GetAppIcon style={{ color: '#757575' }} />
          </IconButton>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: "none", mb: 2 }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#fff" }}>
              {columns
                .filter((col) => col.field !== "otp")
                .map((column, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      fontWeight: 600,
                      color: "#555",
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    {column.title}
                  </TableCell>
                ))}
              <TableCell sx={{ fontWeight: 600, color: "#555" }}>
                {t("actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.filter((col) => col.field !== "otp").length + 1
                  }
                  align="center"
                >
                  <Typography sx={{ py: 2, color: "#777" }}>
                    {t("loading") || "Loading..."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredData?.length > 0 ? (
              dataToDisplay().map((row, index) => (
                <TableRow
                  key={index}
                  hover
                  onClick={() => handleRowClick(row, index)}
                  selected={selectedRow === index}
                  sx={{
                    cursor: "pointer",
                    "&.Mui-selected": {
                      backgroundColor: "rgba(230, 230, 250, 0.2)",
                    },
                    "&:hover": {
                      backgroundColor: "rgba(230, 230, 250, 0.1)",
                    },
                    borderBottom: "1px solid #eee",
                  }}
                >
                  {columns
                    .filter((col) => col.field !== "otp")
                    .map((column, colIndex) => (
                      <TableCell
                        key={colIndex}
                        sx={{
                          py: 2,
                          color: column.field === "status" ? undefined : "#555",
                        }}
                      >
                        {column.field === "status"
                          ? getStatusChip(row[column.field])
                          : column.field === "bookingDate" ||
                            column.field === "tripdate"
                          ? formatDate(row[column.field], currentLang)
                          : column.field === "carType" ||
                            column.field === "vehicleType"
                          ? t(row[column.field].toLowerCase()) // Translate vehicle type
                          : column.field === "trip_cost" ||
                            column.field === "cost" ||
                            column.field === "driver_share"
                          ? settings && settings.symbol
                            ? settings.swipe_symbol === false
                              ? settings.symbol + row[column.field]
                              : row[column.field] + settings.symbol
                            : row[column.field]
                          : row[column.field]}
                      </TableCell>
                    ))}
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {/* Split button for bid/cancel action */}
                      <Box
                        sx={{
                          display: "flex",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          overflow: "hidden",
                        }}
                      >
                        {/* Left side - Bid icon - only active when driver offers exist */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: "6px 10px",
                            borderRight: "1px solid #ddd",
                            cursor:
                              row.status === "NEW" &&
                              row.driverOffers &&
                              Object.keys(row.driverOffers || {}).length > 0
                                ? "pointer"
                                : "not-allowed",
                            opacity:
                              row.status === "NEW" &&
                              row.driverOffers &&
                              Object.keys(row.driverOffers || {}).length > 0
                                ? 1
                                : 0.5,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();

                            if (
                              row.status === "NEW" &&
                              row.driverOffers &&
                              Object.keys(row.driverOffers || {}).length > 0
                            ) {
                              if (onSelectBid) {
                                const rowWithTableData = {
                                  ...row,
                                  tableData: { id: index },
                                };
                                onSelectBid(rowWithTableData);
                              }
                            }
                          }}
                        >
                          <img
                            src={BidIcon}
                            alt="Bid"
                            style={{
                              width: "16px",
                              height: "16px",
                              filter:
                                row.status === "NEW" &&
                                row.driverOffers &&
                                Object.keys(row.driverOffers || {}).length > 0
                                  ? "none"
                                  : "grayscale(100%)",
                            }}
                          />
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: "6px 10px",
                            cursor: !(
                              row.status === "NEW" || row.status === "ACCEPTED"
                            )
                              ? "not-allowed"
                              : "pointer",
                            color: !(
                              row.status === "NEW" || row.status === "ACCEPTED"
                            )
                              ? "#ccc"
                              : "#ff5252",
                            bgcolor: !(
                              row.status === "NEW" || row.status === "ACCEPTED"
                            )
                              ? "#f9f9f9"
                              : "white",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              row.status === "NEW" ||
                              row.status === "ACCEPTED"
                            ) {
                              onCancelBooking && onCancelBooking(row);
                            }
                          }}
                        >
                          <img
                            src={CancelIcon}
                            alt="Cancel"
                            style={{
                              width: "16px",
                              height: "16px",
                              filter: !(
                                row.status === "NEW" ||
                                row.status === "ACCEPTED"
                              )
                                ? "grayscale(100%)"
                                : "none",
                              opacity: !(
                                row.status === "NEW" ||
                                row.status === "ACCEPTED"
                              )
                                ? 0.5
                                : 1,
                            }}
                          />
                        </Box>
                      </Box>

                      <IconButton
                        size="small"
                        sx={{
                          color: "#555",
                          border: "none",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewDetails && onViewDetails(row);
                        }}
                      >
                        <ArrowForwardIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.filter((col) => col.field !== "otp").length + 1
                  }
                  align="center"
                >
                  <Typography sx={{ py: 2, color: "#777" }}>
                    {searchQuery
                      ? t("no_results_found") || "No results found"
                      : t("blank_message") || "No bookings found"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            size="small"
            sx={{ height: "32px" }}
          >
            <MenuItem value={8}>8 {t("rows")}</MenuItem>
            <MenuItem value={16}>16 {t("rows")}</MenuItem>
            <MenuItem value={24}>24 {t("rows")}</MenuItem>
            <MenuItem value={50}>50 {t("rows")}</MenuItem>
            <MenuItem value={100}>100 {t("rows")}</MenuItem>
          </Select>
          <Typography variant="body2" color="text.secondary">
            {t("per_page")}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            {paginationLabel}
          </Typography>
          <Pagination
            count={isSearchActive ? 1 : totalPages}
            page={isSearchActive ? 1 : currentPageNumber}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
            size="small"
            disabled={isSearchActive}
          />
        </Box>
      </Box>
    </>
  );
};

export default BookingTable;